import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import {
    Alert,
    Box,
    Button,
    Chip,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Skeleton,
    Stack,
    Tab,
    Tabs,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TextField,
    Typography,
} from "@mui/material";
import Popup from "../../../components/Popup";
import { useHomeroomTeacherTerm } from "../../../hooks/useHomeroomTeacherTerm";
import { StyledTableCell, StyledTableRow } from "../../../components/styles";
import { getApiBaseUrl } from "../../../config/api";
import { getSchoolIdForUser } from "../../../utils/userContext";
import {
    approveEnrollment,
    fetchPendingEnrollments,
    fetchSchoolDeclarations,
    rejectEnrollment,
} from "../../../utils/enrollmentApi";
import {
    approveStudentApplication,
    fetchSchoolApplications,
    rejectStudentApplication,
} from "../../../utils/studentApplicationApi";

const STATUS_CHIP = {
    Pending: "warning",
    Verified: "success",
    Rejected: "error",
    Submitted: "info",
    Flagged: "error",
    Draft: "default",
};

function EnrollmentReview() {
    const { currentUser } = useSelector((s) => s.user);
    const schoolId = getSchoolIdForUser(currentUser);
    const adminId = currentUser?._id || currentUser?.id;
    const homeroomTerm = useHomeroomTeacherTerm();

    const [tab, setTab] = useState(0);
    const [pending, setPending] = useState([]);
    const [declarations, setDeclarations] = useState([]);
    const [applications, setApplications] = useState([]);
    const [allClasses, setAllClasses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);

    const [rejectOpen, setRejectOpen] = useState(false);
    const [rejectStudentId, setRejectStudentId] = useState(null);
    const [rejectionNote, setRejectionNote] = useState("");

    const [rejectAppOpen, setRejectAppOpen] = useState(false);
    const [rejectAppId, setRejectAppId] = useState(null);
    const [appRejectionNote, setAppRejectionNote] = useState("");

    const [approveAppOpen, setApproveAppOpen] = useState(false);
    const [approveAppId, setApproveAppId] = useState(null);
    const [approveClassId, setApproveClassId] = useState("");
    const [approvePassword, setApprovePassword] = useState("");

    const [verifyOpen, setVerifyOpen] = useState(false);
    const [verifyDeclId, setVerifyDeclId] = useState(null);
    const [adminCountedTotal, setAdminCountedTotal] = useState("");
    const [verifyNotes, setVerifyNotes] = useState("");

    const [classFilter, setClassFilter] = useState("All");
    const [showPopup, setShowPopup] = useState(false);
    const [message, setMessage] = useState("");
    const [loadWarning, setLoadWarning] = useState("");

    const load = useCallback(async () => {
        if (!schoolId) return;
        setLoading(true);
        setLoadWarning("");
        try {
            const [{ list: pendingList, source }, declList, appList, classRes] = await Promise.all([
                fetchPendingEnrollments(schoolId),
                fetchSchoolDeclarations(schoolId),
                fetchSchoolApplications(schoolId, { status: "Submitted" }).catch(() => []),
                axios.get(`${getApiBaseUrl()}/SclassList/${schoolId}`).catch(() => ({ data: [] })),
            ]);
            setPending(pendingList);
            setDeclarations(declList);
            setApplications(Array.isArray(appList) ? appList : []);
            setAllClasses(Array.isArray(classRes.data) ? classRes.data : []);
            if (source === "error" && pendingList.length === 0) {
                setLoadWarning(
                    "Could not load pending enrollments. Deploy the Enrollment function on Supabase or check the database migration for enrollmentStatus.",
                );
            } else if (source === "students" && pendingList.length > 0) {
                setLoadWarning(
                    "Showing pending pupils from the student list (Enrollment API unavailable). Approve still works if you deploy Enrollment.",
                );
            }
        } catch {
            setPending([]);
            setDeclarations([]);
            setApplications([]);
            setAllClasses([]);
            setLoadWarning("Could not load enrollment data.");
        } finally {
            setLoading(false);
        }
    }, [schoolId]);

    useEffect(() => {
        load();
    }, [load]);

    const classIdForStudent = (s) => {
        if (s.sclass && typeof s.sclass === "object") {
            return s.sclass._id || s.sclass.id;
        }
        if (s.sclassName && typeof s.sclassName === "object") {
            return s.sclassName._id || s.sclassName.id;
        }
        return typeof s.sclassName === "string" ? s.sclassName : null;
    };

    const classLabelForStudent = (s) => {
        if (s.sclass?.sclassName) return s.sclass.sclassName;
        if (s.sclassName && typeof s.sclassName === "object") return s.sclassName.sclassName || "Class";
        return "Class";
    };

    const classOptions = useMemo(() => {
        const names = new Map();
        pending.forEach((s) => {
            const id = classIdForStudent(s);
            const label = classLabelForStudent(s);
            if (id) names.set(id, label);
        });
        return [{ id: "All", label: "All classes" }, ...[...names.entries()].map(([id, label]) => ({ id, label }))];
    }, [pending]);

    const filteredPending = useMemo(() => {
        if (classFilter === "All") return pending;
        return pending.filter((s) => classIdForStudent(s) === classFilter);
    }, [pending, classFilter]);

    const handleApprove = async (studentId) => {
        setActionLoading(true);
        try {
            await approveEnrollment(studentId, adminId);
            setMessage("Enrollment verified. School fees assigned from active fee structures.");
            setShowPopup(true);
            load();
        } catch (err) {
            setMessage(err.response?.data?.message || "Could not approve enrollment. Deploy Student and Enrollment functions on Supabase.");
            setShowPopup(true);
        } finally {
            setActionLoading(false);
        }
    };

    const handleRejectConfirm = async () => {
        if (!rejectStudentId) return;
        setActionLoading(true);
        try {
            await rejectEnrollment(rejectStudentId, {
                approvedById: adminId,
                rejectionNote,
            });
            setRejectOpen(false);
            setRejectionNote("");
            setMessage("Enrollment rejected.");
            setShowPopup(true);
            load();
        } catch (err) {
            setMessage(err.response?.data?.message || "Could not reject enrollment.");
            setShowPopup(true);
        } finally {
            setActionLoading(false);
        }
    };

    const openVerifyDecl = (row) => {
        setVerifyDeclId(row._id || row.id);
        setAdminCountedTotal(String(row.declaredTotal ?? ""));
        setVerifyNotes("");
        setVerifyOpen(true);
    };

    const handleVerifyDecl = async () => {
        if (!verifyDeclId) return;
        setActionLoading(true);
        try {
            const { data } = await axios.put(
                `${getApiBaseUrl()}/Enrollment/declaration/${verifyDeclId}/verify`,
                {
                    approvedById: adminId,
                    adminCountedTotal: Number(adminCountedTotal),
                    notes: verifyNotes,
                },
            );
            setVerifyOpen(false);
            setMessage(data.message || "Declaration verified.");
            setShowPopup(true);
            load();
        } catch (err) {
            setMessage(err.response?.data?.message || "Could not verify");
            setShowPopup(true);
        } finally {
            setActionLoading(false);
        }
    };

    const flaggedDecls = declarations.filter((d) => d.status === "Flagged" || d.status === "Submitted");

    const openApproveApp = (row) => {
        const id = row._id || row.id;
        const preferred = row.desiredClassId || row.desiredClass?._id || row.desiredClass?.id || "";
        setApproveAppId(id);
        setApproveClassId(preferred || "");
        setApprovePassword("");
        setApproveAppOpen(true);
    };

    const handleApproveApp = async () => {
        if (!approveAppId || !approveClassId) return;
        setActionLoading(true);
        try {
            const data = await approveStudentApplication(approveAppId, {
                approvedById: adminId,
                classId: approveClassId,
                password: approvePassword.trim() || undefined,
            });
            setApproveAppOpen(false);
            const pwdNote = data.temporaryPassword
                ? ` Temporary password: ${data.temporaryPassword} (share with guardian).`
                : "";
            setMessage((data.message || "Application approved.") + pwdNote);
            setShowPopup(true);
            load();
        } catch (err) {
            setMessage(err.response?.data?.message || "Could not approve application.");
            setShowPopup(true);
        } finally {
            setActionLoading(false);
        }
    };

    const handleRejectAppConfirm = async () => {
        if (!rejectAppId) return;
        setActionLoading(true);
        try {
            await rejectStudentApplication(rejectAppId, {
                approvedById: adminId,
                rejectionNote: appRejectionNote,
            });
            setRejectAppOpen(false);
            setAppRejectionNote("");
            setMessage("Application rejected.");
            setShowPopup(true);
            load();
        } catch (err) {
            setMessage(err.response?.data?.message || "Could not reject application.");
            setShowPopup(true);
        } finally {
            setActionLoading(false);
        }
    };

    const formatAppDate = (value) => {
        if (!value) return "—";
        const d = new Date(value);
        return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
    };

    return (
        <Container maxWidth="lg" sx={{ py: 3 }}>
            <Typography variant="h5" fontWeight={800} gutterBottom>
                Enrollment verification
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Approve {homeroomTerm.enrollHint} enrollments before fees apply. Reconcile class roll declarations so no learners
                are kept off the system for fee theft.
            </Typography>

            <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                <strong>Anti–fee theft checks:</strong> only <strong>Verified</strong> pupils get fee records. Pending +
                Verified count toward your plan limit ({homeroomTerm.theRole} enrollments only).
                <br />
                <br />
                1) <strong>Declared roll &gt; system:</strong> {homeroomTerm.roleTitleCaps} claims more learners on paper
                than are registered — approve pending enrollments or investigate.
                <br />
                2) <strong>Physical count &gt; system:</strong> you count more pupils in class than are registered — they
                may be paying fees off the books. Register and verify them before collection.
            </Alert>

            {loadWarning ? (
                <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setLoadWarning("")}>
                    {loadWarning}
                </Alert>
            ) : null}

            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
                <Tab label={`Pending enrollments (${pending.length})`} />
                <Tab label={`Public applications (${applications.length})`} />
                <Tab label={`Class roll declarations (${flaggedDecls.length})`} />
            </Tabs>

            {tab === 0 ? (
                <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
                    <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel>Class</InputLabel>
                            <Select label="Class" value={classFilter} onChange={(e) => setClassFilter(e.target.value)}>
                                {classOptions.map((o) => (
                                    <MenuItem key={o.id} value={o.id}>
                                        {o.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <StyledTableRow>
                                    <StyledTableCell>Student</StyledTableCell>
                                    <StyledTableCell>Roll</StyledTableCell>
                                    <StyledTableCell>Class</StyledTableCell>
                                    <StyledTableCell>Submitted by</StyledTableCell>
                                    <StyledTableCell>Guardian phone</StyledTableCell>
                                    <StyledTableCell align="right">Actions</StyledTableCell>
                                </StyledTableRow>
                            </TableHead>
                            <TableBody>
                                {loading
                                    ? Array.from({ length: 3 }).map((_, i) => (
                                          <StyledTableRow key={i}>
                                              <StyledTableCell colSpan={6}>
                                                  <Skeleton height={32} />
                                              </StyledTableCell>
                                          </StyledTableRow>
                                      ))
                                    : null}
                                {!loading && filteredPending.length === 0 ? (
                                    <StyledTableRow>
                                        <StyledTableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                            No pending enrollments.
                                        </StyledTableCell>
                                    </StyledTableRow>
                                ) : null}
                                {!loading &&
                                    filteredPending.map((st) => {
                                        const sid = st._id || st.id;
                                        return (
                                            <StyledTableRow key={sid}>
                                                <StyledTableCell>{st.name}</StyledTableCell>
                                                <StyledTableCell>{st.rollNum}</StyledTableCell>
                                                <StyledTableCell>{classLabelForStudent(st) || "—"}</StyledTableCell>
                                                <StyledTableCell>
                                                    {st.submittedByTeacher?.name || "—"}
                                                </StyledTableCell>
                                                <StyledTableCell>{st.guardianPhone || "—"}</StyledTableCell>
                                                <StyledTableCell align="right">
                                                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                        <Button
                                                            size="small"
                                                            variant="contained"
                                                            disabled={actionLoading}
                                                            onClick={() => handleApprove(sid)}
                                                        >
                                                            Verify
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            color="error"
                                                            variant="outlined"
                                                            disabled={actionLoading}
                                                            onClick={() => {
                                                                setRejectStudentId(sid);
                                                                setRejectOpen(true);
                                                            }}
                                                        >
                                                            Reject
                                                        </Button>
                                                    </Stack>
                                                </StyledTableCell>
                                            </StyledTableRow>
                                        );
                                    })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            ) : tab === 1 ? (
                <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
                    <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
                        <Typography variant="body2" color="text.secondary">
                            Applications submitted from your public school page. Approve to register the learner and assign
                            them to a class; school fees are applied automatically.
                        </Typography>
                    </Box>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <StyledTableRow>
                                    <StyledTableCell>Applicant</StyledTableCell>
                                    <StyledTableCell>Guardian</StyledTableCell>
                                    <StyledTableCell>Phone</StyledTableCell>
                                    <StyledTableCell>Preferred class</StyledTableCell>
                                    <StyledTableCell>Submitted</StyledTableCell>
                                    <StyledTableCell align="right">Actions</StyledTableCell>
                                </StyledTableRow>
                            </TableHead>
                            <TableBody>
                                {loading
                                    ? Array.from({ length: 3 }).map((_, i) => (
                                          <StyledTableRow key={i}>
                                              <StyledTableCell colSpan={6}>
                                                  <Skeleton height={32} />
                                              </StyledTableCell>
                                          </StyledTableRow>
                                      ))
                                    : null}
                                {!loading && applications.length === 0 ? (
                                    <StyledTableRow>
                                        <StyledTableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                            No public applications awaiting review.
                                        </StyledTableCell>
                                    </StyledTableRow>
                                ) : null}
                                {!loading &&
                                    applications.map((app) => {
                                        const aid = app._id || app.id;
                                        return (
                                            <StyledTableRow key={aid}>
                                                <StyledTableCell>
                                                    <Typography variant="body2" fontWeight={600}>
                                                        {app.name}
                                                    </Typography>
                                                    {app.educationLevel ? (
                                                        <Typography variant="caption" color="text.secondary">
                                                            {app.educationLevel}
                                                        </Typography>
                                                    ) : null}
                                                </StyledTableCell>
                                                <StyledTableCell>{app.guardianName || "—"}</StyledTableCell>
                                                <StyledTableCell>{app.guardianPhone || "—"}</StyledTableCell>
                                                <StyledTableCell>
                                                    {app.desiredClass?.sclassName || "No preference"}
                                                </StyledTableCell>
                                                <StyledTableCell>{formatAppDate(app.createdAt)}</StyledTableCell>
                                                <StyledTableCell align="right">
                                                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                        <Button
                                                            size="small"
                                                            variant="contained"
                                                            disabled={actionLoading}
                                                            onClick={() => openApproveApp(app)}
                                                        >
                                                            Approve & assign class
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            color="error"
                                                            variant="outlined"
                                                            disabled={actionLoading}
                                                            onClick={() => {
                                                                setRejectAppId(aid);
                                                                setAppRejectionNote("");
                                                                setRejectAppOpen(true);
                                                            }}
                                                        >
                                                            Reject
                                                        </Button>
                                                    </Stack>
                                                </StyledTableCell>
                                            </StyledTableRow>
                                        );
                                    })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            ) : (
                <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <StyledTableRow>
                                    <StyledTableCell>Class</StyledTableCell>
                                    <StyledTableCell>{homeroomTerm.roleTitleCaps}</StyledTableCell>
                                    <StyledTableCell>Declared</StyledTableCell>
                                    <StyledTableCell>Director count</StyledTableCell>
                                    <StyledTableCell>Status</StyledTableCell>
                                    <StyledTableCell align="right">Actions</StyledTableCell>
                                </StyledTableRow>
                            </TableHead>
                            <TableBody>
                                {loading
                                    ? Array.from({ length: 2 }).map((_, i) => (
                                          <StyledTableRow key={i}>
                                              <StyledTableCell colSpan={6}>
                                                  <Skeleton height={32} />
                                              </StyledTableCell>
                                          </StyledTableRow>
                                      ))
                                    : null}
                                {!loading && flaggedDecls.length === 0 ? (
                                    <StyledTableRow>
                                        <StyledTableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                            No declarations awaiting review.
                                        </StyledTableCell>
                                    </StyledTableRow>
                                ) : null}
                                {!loading &&
                                    flaggedDecls.map((row) => {
                                        const id = row._id || row.id;
                                        return (
                                            <StyledTableRow key={id}>
                                                <StyledTableCell>{row.class?.sclassName || "—"}</StyledTableCell>
                                                <StyledTableCell>
                                                    {row.submittedByTeacher?.name || "—"}
                                                </StyledTableCell>
                                                <StyledTableCell>{row.declaredTotal}</StyledTableCell>
                                                <StyledTableCell>
                                                    {row.adminCountedTotal ?? "—"}
                                                </StyledTableCell>
                                                <StyledTableCell>
                                                    <Chip
                                                        size="small"
                                                        label={row.status}
                                                        color={STATUS_CHIP[row.status] || "default"}
                                                    />
                                                </StyledTableCell>
                                                <StyledTableCell align="right">
                                                    <Button
                                                        size="small"
                                                        variant="contained"
                                                        disabled={actionLoading}
                                                        onClick={() => openVerifyDecl(row)}
                                                    >
                                                        Verify count
                                                    </Button>
                                                </StyledTableCell>
                                            </StyledTableRow>
                                        );
                                    })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}

            <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Reject enrollment</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        label="Reason (optional)"
                        value={rejectionNote}
                        onChange={(e) => setRejectionNote(e.target.value)}
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRejectOpen(false)}>Cancel</Button>
                    <Button color="error" variant="contained" onClick={handleRejectConfirm} disabled={actionLoading}>
                        Reject
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={approveAppOpen} onClose={() => setApproveAppOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Approve application & assign class</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Select the class for this learner. A student ID and portal login will be created automatically.
                    </Typography>
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Class</InputLabel>
                        <Select
                            label="Class"
                            value={approveClassId}
                            onChange={(e) => setApproveClassId(e.target.value)}
                        >
                            {allClasses.map((c) => {
                                const cid = c._id || c.id;
                                return (
                                    <MenuItem key={cid} value={cid}>
                                        {c.sclassName}
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </FormControl>
                    <TextField
                        fullWidth
                        label="Portal password (optional)"
                        helperText="Leave blank to generate a temporary password"
                        value={approvePassword}
                        onChange={(e) => setApprovePassword(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setApproveAppOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleApproveApp}
                        disabled={actionLoading || !approveClassId}
                    >
                        Approve & enroll
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={rejectAppOpen} onClose={() => setRejectAppOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Reject application</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        label="Reason (optional)"
                        value={appRejectionNote}
                        onChange={(e) => setAppRejectionNote(e.target.value)}
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRejectAppOpen(false)}>Cancel</Button>
                    <Button color="error" variant="contained" onClick={handleRejectAppConfirm} disabled={actionLoading}>
                        Reject
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={verifyOpen} onClose={() => setVerifyOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Verify class roll count</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Enter the number of learners you physically counted in this class. If this is higher than
                        verified + pending on the system, the class stays flagged.
                    </Typography>
                    <TextField
                        fullWidth
                        type="number"
                        label="Learners you counted"
                        value={adminCountedTotal}
                        onChange={(e) => setAdminCountedTotal(e.target.value)}
                        inputProps={{ min: 0 }}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        label="Notes"
                        value={verifyNotes}
                        onChange={(e) => setVerifyNotes(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setVerifyOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleVerifyDecl} disabled={actionLoading}>
                        Save verification
                    </Button>
                </DialogActions>
            </Dialog>

            <Popup message={message} showPopup={showPopup} setShowPopup={setShowPopup} />
        </Container>
    );
}

export default EnrollmentReview;
